import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Sparkles, ArrowUpRight } from "lucide-react";
import { useReducedMotion, AnimatePresence, motion } from "framer-motion";
import { useFeedItems } from "@/hooks/useSiteContent";

const PAGE_SIZE = 5;
const ROTATE_MS = 6000;

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

export default function LiveFeed({ size = "compact" }) {
  const reduce = useReducedMotion();
  const { data, loading } = useFeedItems();
  const items = data?.items || [];
  const eventsCount = data?.events_count ?? 0;

  const pages = useMemo(() => (items.length ? chunk(items, PAGE_SIZE) : []), [items]);
  const [pageIdx, setPageIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [animated, setAnimated] = useState(false);

  // Reset when data changes
  useEffect(() => {
    setPageIdx(0);
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 250);
    return () => clearTimeout(t);
  }, [items.length]);

  // Auto-rotate through pages when there are more than 5 items
  useEffect(() => {
    if (pages.length <= 1 || paused) return;
    const t = setInterval(() => {
      setAnimated(false);
      setPageIdx((i) => (i + 1) % pages.length);
      // re-trigger progress bar animation
      requestAnimationFrame(() => setAnimated(true));
    }, ROTATE_MS);
    return () => clearInterval(t);
  }, [pages.length, paused]);

  const currentPage = pages[pageIdx] || [];

  const isHeroSize = size === "hero";
  const wrapperClass = isHeroSize
    ? "relative w-full rounded-3xl border border-brand-border bg-brand-ivory backdrop-blur-xl shadow-[0_50px_120px_-40px_rgba(87,102,44,0.45)]"
    : "relative w-full rounded-2xl border border-brand-border bg-brand-ivory backdrop-blur-xl shadow-[0_30px_80px_-30px_rgba(122,142,74,0.35)]";

  return (
    <div
      data-testid="hero-live-feed"
      className={wrapperClass}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-px rounded-2xl"
        style={{
          background:
            "radial-gradient(60% 80% at 50% 0%, rgba(122,142,74,0.15), transparent 60%)",
        }}
      />

      <div
        className={`relative flex items-center justify-between ${
          isHeroSize ? "px-7 pt-7 pb-3" : "px-6 pt-6 pb-2"
        }`}
      >
        <div
          className={`font-semibold tracking-[0.24em] uppercase text-brand-olive ${
            isHeroSize ? "text-xs" : "text-[11px]"
          }`}
        >
          Live Intelligence Feed
        </div>
        <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-brand-muted">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-brand-olive opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-brand-olive" />
          </span>
          Live
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.ul
          key={pageIdx}
          initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduce ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className={`relative py-4 ${isHeroSize ? "px-4 space-y-1.5" : "px-3 space-y-1"}`}
        >
          {loading && !currentPage.length ? (
            <li className="px-4 py-6 text-xs text-brand-quiet">Loading signals…</li>
          ) : currentPage.length === 0 ? (
            <li className="px-4 py-6 text-xs text-brand-quiet">
              No live signals right now.
            </li>
          ) : (
            currentPage.map((s, idx) => (
              <li
                key={`${s.title}-${idx}`}
                data-testid={`live-signal-${idx}`}
                className={`group flex items-center gap-4 rounded-xl hover:bg-brand-border/50 transition-colors ${
                  isHeroSize ? "px-5 py-4" : "px-4 py-3"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-brand-deep font-medium truncate ${
                      isHeroSize ? "text-base" : "text-sm"
                    }`}
                  >
                    {s.title}
                  </p>
                  <p
                    className={`mt-1 text-brand-quiet tracking-wide font-mono uppercase ${
                      isHeroSize ? "text-[11px]" : "text-[11px]"
                    }`}
                  >
                    ◎ {s.source_event}
                  </p>
                </div>
                <div className={`flex items-center gap-3 ${isHeroSize ? "w-40" : "w-32"}`}>
                  <div className="flex-1 h-[3px] bg-brand-border overflow-hidden rounded-full">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-brand-olive to-brand-accent transition-[width] duration-1000 ease-out"
                      style={{
                        width: animated ? `${s.match_strength}%` : "0%",
                      }}
                    />
                  </div>
                  <span className="text-xs text-brand-warm font-mono tabular-nums w-9 text-right">
                    {s.match_strength}%
                  </span>
                </div>
              </li>
            ))
          )}
        </motion.ul>
      </AnimatePresence>

      <div
        className={`relative border-t border-brand-border flex items-center justify-between ${
          isHeroSize ? "px-7 py-5" : "px-6 py-4"
        }`}
      >
        <div className="flex items-center gap-2 text-xs text-brand-muted">
          <Sparkles className="w-3.5 h-3.5 text-brand-olive" strokeWidth={1.6} />
          <span data-testid="hero-live-feed-events-count">
            Updated from {eventsCount} industry event{eventsCount === 1 ? "" : "s"}
          </span>
        </div>
        {pages.length > 1 && (
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {pages.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === pageIdx ? "w-4 bg-brand-olive" : "w-1 bg-brand-border"
                }`}
              />
            ))}
          </div>
        )}
        <Link
          to="/platform"
          data-testid="hero-live-feed-explore"
          className="inline-flex items-center gap-1 text-xs text-brand-olive hover:text-brand-olive transition-colors"
        >
          Explore
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <Radio
        aria-hidden="true"
        className="absolute -top-3 -left-3 w-6 h-6 text-brand-olive/0"
        strokeWidth={1.4}
      />
    </div>
  );
}
